import { useEffect, useState, useMemo } from "react";
import { Box, Typography, Grid } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import FilterBtn from "../../../../../Assets/Icons/btn-filters.png";
import FilterBtnUp from "../../../../../Assets/Icons/btn-filters-up.png";
import { useNotification } from "../../../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../../../Hooks/useApiErrorHandler";
import httpclient from "../../../../../Utils";
import { createFilterHandler } from "../../../../../Utils/FilterUtils";

import { columns } from "./columns";
import { rows } from "./data";
import Filter from "./Filter";

const dataGridCustomStyles = {
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f5f5f5",
    borderRadius: 1,
  },
  "& .MuiDataGrid-cell": {
    color: "#383E49",
    fontWeight: 500,
  },
  "& .onhold-row": {
    backgroundColor: "#F45B691A",
    color: "#F45B69",
    "& .MuiDataGrid-cell": {
      color: "#F45B69",
      fontWeight: 500,
    },
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontSize: "14px",
    fontWeight: 500,
    color: "#667085",
  },
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  "& .absent-row": {
    backgroundColor: "#E1913321",
    color: "#E19133",
    "& .MuiDataGrid-cell": {
      color: "#E19133",
      fontWeight: 300,
    },
  },
};

const INITIAL_FILTER_STATE = {
  employeeName: "",
  deviceNumber: "",
  totalOrdersMin: "",
  totalOrdersMax: "",
  priorityOrdersMin: "",
  priorityOrdersMax: "",
  orderLinesMin: "",
  orderLinesMax: "",
  totalItemsMin: "",
  totalItemsMax: "",
  status: "",
};

const fieldMappings = {
  employeeName: ["employeeName", "$eq"],
  deviceNumber: ["deviceNumber", "$eq"],
  status: ["status", "$eq"],
  department: ["department", "$eq"],
};

const rangeFields = {
  totalOrders: {
    apiField: "totalOrders",
    minField: "totalOrdersMin",
    maxField: "totalOrdersMax",
  },
  priorityOrders: {
    apiField: "priorityOrders",
    minField: "priorityOrdersMin",
    maxField: "priorityOrdersMax",
  },
  orderLines: {
    apiField: "orderLines",
    minField: "orderLinesMin",
    maxField: "orderLinesMax",
  },
  totalItems: {
    apiField: "totalItems",
    minField: "totalItemsMin",
    maxField: "totalItemsMax",
  },
};

export default function PickersOverview() {
  const [pickers, setPickers] = useState([]);

  const [filterOpen, setFilterOpen] = useState(false);

  const [filterData, setFilterData] = useState(INITIAL_FILTER_STATE);
  const [submittedData, setSubmittedData] = useState({});

  const [loading, setLoading] = useState(false);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  const fetchPickers = async () => {
    const API_URL = "getPlugins.php";

    try {
      const res = await httpclient.get(API_URL);

      const { data } = res;

      if (data.status === 200) {
        setPickers(data.data);
      } else {
        showNotification("error", data.msg);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // fetchPickers();
  }, []);

  const handleChangeFilter = (field) => (eventOrValue) => {
    // Handle Select dropdown (eventOrValue is the event)
    if (field === "status") {
      setFilterData((prev) => ({
        ...prev,
        [field]: eventOrValue.target.value,
      }));
      return;
    }

    // Handle Autocomplete (eventOrValue is the selected value)
    if (field === "designation") {
      setFilterData((prev) => ({
        ...prev,
        [field]: eventOrValue || "",
      }));
      return;
    }

    // Handle regular text/number inputs (eventOrValue is the event)
    setFilterData((prev) => ({
      ...prev,
      [field]: eventOrValue.target.value,
    }));
  };

  const handleFilter = createFilterHandler({
    setLoading,
    setSubmittedData,
    setResults: setPickers,
    showNotification,
    handleApiError,
    httpClient: httpclient,
    endpoint: "getPlugins.php",
    fieldMappings,
    rangeFields,
  });

  const onFilterClick = () => {
    handleFilter(filterData);
  };

  const handleRemove = (field) => {
    setFilterData((prev) => ({
      ...prev,
      [field]: "",
    }));
    setSubmittedData((prev) => ({
      ...prev,
      [field]: undefined,
    }));
  };

  const handleClearFilters = () => {
    setFilterData(INITIAL_FILTER_STATE);
    setSubmittedData({});
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (
        submittedData.employeeName &&
        !row.employeeName
          .toLowerCase()
          .includes(submittedData.employeeName.toLowerCase())
      ) {
        return false;
      }

      if (
        submittedData.deviceNumber &&
        !row.deviceNumber
          .toLowerCase()
          .includes(submittedData.deviceNumber.toLowerCase())
      ) {
        return false;
      }

      const totalOrders = Number(row.totalOrders);
      if (
        submittedData.totalOrdersMin &&
        totalOrders < Number(submittedData.totalOrdersMin)
      ) {
        return false;
      }

      if (
        submittedData.totalOrdersMax &&
        totalOrders > Number(submittedData.totalOrdersMax)
      ) {
        return false;
      }

      const priorityOrders =
        row.priorityOrders === "-" ? 0 : Number(row.priorityOrders);
      if (
        submittedData.priorityOrdersMin &&
        priorityOrders < Number(submittedData.priorityOrdersMin)
      ) {
        return false;
      }
      if (
        submittedData.priorityOrdersMax &&
        priorityOrders > Number(submittedData.priorityOrdersMax)
      ) {
        return false;
      }

      const orderLines = Number(row.orderLines);

      if (
        submittedData.orderLinesMin &&
        orderLines < Number(submittedData.orderLinesMin)
      ) {
        return false;
      }
      if (
        submittedData.orderLinesMax &&
        orderLines > Number(submittedData.orderLinesMax)
      ) {
        return false;
      }

      const totalItems = Number(row.totalItems);

      if (
        submittedData.totalItemsMin &&
        totalItems < Number(submittedData.totalItemsMin)
      ) {
        return false;
      }

      if (
        submittedData.totalItemsMax &&
        totalItems > Number(submittedData.totalItemsMax)
      ) {
        return false;
      }

      if (submittedData.status && row.status !== submittedData.status) {
        return false;
      }

      return true;
    });
  }, [submittedData]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        marginTop: "64px",
        padding: "24px 16px",
        backgroundColor: "white",
        borderRadius: "8px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px",
          backgroundColor: "white",
          marginBottom: "16px",
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontSize: "20px", fontWeight: 500, color: "#383E49" }}
        >
          Pickers Overview Today
        </Typography>
        <Grid
          item
          xs={12}
          md={4}
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
        >
          <img
            src={filterOpen ? FilterBtnUp : FilterBtn}
            alt="Filter"
            style={{ cursor: "pointer" }}
            onClick={() => setFilterOpen(!filterOpen)}
          />
        </Grid>
      </Box>
      <Filter
        filterOpen={filterOpen}
        filterData={filterData}
        handleChangeFilter={handleChangeFilter}
        handleFilter={onFilterClick}
        handleClearFilters={handleClearFilters}
        handleRemove={handleRemove}
        submittedData={submittedData}
      />
      <Box sx={{ width: "100%" }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          getRowClassName={(params) =>
            params.row.status === "Absent" ? "absent-row" : ""
          }
          pageSizeOptions={[10]} // Community version uses pageSizeOptions
          sx={dataGridCustomStyles}
        />
      </Box>
    </Box>
  );
}
